import { useI18n as useGlobalI18n } from "vue-i18n";
import { logFrontendMessage } from "@/apps/credit-line/services/network.service";
import { IFrontendLogMessageRequest } from "@/apps/credit-line/types/requests";

const safeLogFrontendMessage = (params: IFrontendLogMessageRequest) => {
  try {
    logFrontendMessage(params).catch(() => {
      // fire and forget — do not throw on logging failure
    });
  } catch {
    // fire and forget — do not throw on logging failure
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAppI18n = (appName: string) => {
  const {
    t: globalT,
    locale,
    availableLocales,
    fallbackLocale,
  } = useGlobalI18n();

  // Convert kebab-case to SCREAMING_SNAKE for namespace
  const screamingSnakeNamespace =
    appName === "shared" ? "SHARED" : appName.toUpperCase().replace(/-/g, "_");

  const t = (key: string, values?: Record<string, unknown>) => {
    // Try app namespace first
    const appKey = `${screamingSnakeNamespace}.${key}`;
    const appTranslation = values ? globalT(appKey, values) : globalT(appKey);

    if (appTranslation !== appKey) {
      return appTranslation;
    }

    // Fallback to shared namespace
    const sharedKey = `SHARED.${key}`;
    const sharedTranslation = values
      ? globalT(sharedKey, values)
      : globalT(sharedKey);

    if (sharedTranslation !== sharedKey) {
      return sharedTranslation;
    }

    safeLogFrontendMessage({
      message: `Translation key: ${key} not found in ${appName} or shared translations`,
      level: "error",
      context: { translationKey: key, appName, locale: locale?.value },
    });

    return key;
  };

  const te = (key: string): boolean => {
    const appKey = `${screamingSnakeNamespace}.${key}`;
    if (globalT(appKey) !== appKey) return true;

    const sharedKey = `SHARED.${key}`;
    return globalT(sharedKey) !== sharedKey;
  };

  return {
    t,
    te,
    locale,
    availableLocales,
    fallbackLocale,
  };
};
